import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { TitleContent } from './TitleContent';
import {
  Colors,
  ColorValues,
  Flex,
  IconTooltip,
  Typography,
} from '@banhala/ui-kit';
import {
  convertToDayJs,
  getDayOfWeek,
  isNil,
  isNotNil,
} from 'shared/src/utils';
import { Tooltip } from 'shared/src/component/Tooltip';
import { useGetNextWorkingDay } from 'shared/src/hooks';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';
import { useGetDeliveryOrderDeadlineTime } from '../../../hooks/useGetDeliveryOrderDeadlineTime';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import { isDeliveryTypeIsToday } from '../../../utils/isDeliveryTypeIsToday';
import { useGetNextArrivalDay } from '../hooks/useGetNextArrivalDay';
import { useIsTodayDeliveryOperatingDay } from '../hooks/useIsTodayDeliveryOperatingDay';

export const ArrivalExpectedDay: React.FC = () => {
  const { data: goodsBasic } = useGoodsBasic();

  const { data: goodsDetail } = useGoodsDetail();
  const limitTime = useGetDeliveryOrderDeadlineTime(goodsBasic);
  const limitDayjs = useMemo(
    () => (isNotNil(limitTime) ? convertToDayJs(limitTime) : null),
    [limitTime],
  );

  // 도착 예정일의 기준점은 "실제 출발일" 이고, 이는 RemainTimer 가 안내하는 출발일과
  // 같은 규칙이어야 한다 — 오늘이 영업일이면서 마감 전이면 오늘 출발, 그 외엔 다음 영업일.
  //
  // 기존에는 마감 "시각" 만 봤다. 그래서 토요일처럼 오늘이 영업일이 아닌 날 마감 전이면
  // 토요일을 출발일로 잡아, 상단은 "월요일 출발 예정" 인데 하단은 "월요일 도착 예정" 으로
  // 출발일과 도착일이 같아지는 모순이 생겼다.
  const nextWorkingAfterLimit = useGetNextWorkingDay(limitDayjs);
  const isTodayOperatingDay = useIsTodayDeliveryOperatingDay();
  const effectiveStartDay = useMemo(() => {
    if (limitDayjs === null) return null;

    const isBeforeDeadline = !limitDayjs.isBefore(dayjs());

    return isBeforeDeadline && isTodayOperatingDay
      ? limitDayjs
      : nextWorkingAfterLimit;
  }, [limitDayjs, nextWorkingAfterLimit, isTodayOperatingDay]);

  // 도착 예정일은 effectiveStartDay 기준으로 N 영업일 누적 (native iOS
  // calculateEstimatedDeliveryDate / Android calculateEstimatedArrival 정합).
  const arrivalDay = useGetNextArrivalDay(
    goodsDetail?.goods.market.estimated_delivery_days && effectiveStartDay
      ? effectiveStartDay.add(
          goodsDetail.goods.market.estimated_delivery_days,
          'day',
        )
      : null,
    goodsBasic?.delivery_type_info.delivery_type,
  );

  // Android와 동일하게 SHAK, TODAY 배송 타입일 때만 도착 예정일 표시
  if (!isDeliveryTypeIsToday(goodsBasic)) return null;
  if (isNil(arrivalDay)) return null;

  return (
    <TitleContent title={''}>
      <Flex.Row alignItems={'center'} gap={4}>
        <Typography color={'content_tertiary'} type={'body2'}>
          {arrivalDay.format('M/D')}({getDayOfWeek(arrivalDay)}) 도착 예정이에요
        </Typography>
        <Tooltip
          label={
            'AI가 배송 흐름을 분석한 예측 정보예요.\n교통, 물류 상황에 따라 1~2일 달라질 수 있어요.'
          }
          contentStyle={{
            paddingHorizontal: 14,
            paddingVertical: 12,
            backgroundColor: ColorValues.slate60,
          }}
        >
          <IconTooltip size={16} color={Colors.content_tertiary} />
        </Tooltip>
      </Flex.Row>
    </TitleContent>
  );
};
